import { parseServerInstant } from './mskTime'

// Часовая ось строится по МСК: сервер отдаёт UTC-инстант бакета, здесь он
// форматируется в московские часы. Окно бывает разное — у «Непокрытого спроса»
// это скользящие 24 часа (могут пересечь полночь), у таймлайна вкладка «День»
// это текущие МСК-сутки с 00:00 и полночь внутри неё не встречается. Дата
// показывается один раз, второй строкой под первым бакетом своего дня; реальная
// смена дня внутри окна дополнительно рисуется разделителем (dayDividerEvents).
// Повторять дату на каждой подписи — шум.
const hourFmt = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  hour: '2-digit',
  minute: '2-digit',
})

const dayFmt = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  day: '2-digit',
  month: '2-digit',
})

export type HourlyAxis = {
  /** Apex category labels; an array element renders as two lines. */
  categories: (string | string[])[]
  /**
   * Categories that open a new MSK day, first bucket excluded (nothing to divide
   * at the very edge). Same object identity as in `categories` — Apex resolves an
   * annotation by `labels.indexOf(x)`, so the value must be the exact same one.
   */
  dayStarts: (string | string[])[]
}

export function hourlyAxis(timestamps: string[]): HourlyAxis {
  const categories: (string | string[])[] = []
  const dayStarts: (string | string[])[] = []
  let prevDay = ''

  timestamps.forEach((ts, i) => {
    const d = parseServerInstant(ts)
    const day = dayFmt.format(d)
    const hour = hourFmt.format(d)
    // i > 0: the leftmost bucket opens no new day *inside* the window, so it gets
    // no divider — but it still starts the day shown on the left, hence the date.
    const dayChanged = i > 0 && day !== prevDay
    prevDay = day

    const category = i === 0 || dayChanged ? [hour, day] : hour
    categories.push(category)
    if (dayChanged) dayStarts.push(category)
  })

  return { categories, dayStarts }
}

/**
 * Chart events that draw a vertical divider at every day change.
 *
 * Apex anchors an x-axis annotation on the category's tick — the middle of the
 * column — so the line is shifted half a slot left to land on the boundary
 * between the two days. Half a slot is only known once the grid is measured,
 * hence the annotations are added on mount/update instead of in the options.
 */
export function dayDividerEvents(dayStarts: (string | string[])[], color: string) {
  const draw = (ctx: any) => {
    const labels = ctx?.w?.globals?.labels
    if (!labels?.length) return
    // gridWidth is null until the chart is measured (hidden container, jsdom) —
    // fall back to no shift rather than an NaN offset that hides the line.
    const gridWidth = ctx.w.globals.gridWidth
    const halfSlot = Number.isFinite(gridWidth) && gridWidth > 0 ? gridWidth / labels.length / 2 : 0
    ctx.clearAnnotations()
    dayStarts.forEach((category) =>
      ctx.addXaxisAnnotation(
        {
          x: category,
          offsetX: -halfSlot,
          borderColor: color,
          borderWidth: 2,
          strokeDashArray: 0,
        },
        false
      )
    )
  }

  return { mounted: draw, updated: draw }
}
